import React, { useState, useEffect, useRef } from 'react';
import ExperienceCard from './ExperienceCard';
import { Briefcase } from 'lucide-react';
import { experiences } from '../Constant';
import { calculateTotalExperience } from '../Utility/utils';

const ExperienceTimeline: React.FC = () => {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisibleItems((prev) => {
          const newSet = new Set(prev);

          entries.forEach((entry) => {
            const id = entry.target.getAttribute('data-id');
            if (!id) return;

            if (entry.isIntersecting) {
              newSet.add(id); // Show when visible
            } else {
              newSet.delete(id); // Hide when not visible
            }
          });

          return newSet;
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px 0px 0px',
      }
    );

    itemRefs.current.forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const setItemRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(id, el);
    } else {
      itemRefs.current.delete(id);
    }
  };

  return (
    <div className='bg-gray-900 py-32 px-8'>
      <div className='max-w-4xl mx-auto'>
        {/* Header */}
        <div
          className={`text-center mb-16 transform transition-all duration-700 ease-out ${
            visibleItems.size > 0
              ? 'translate-y-0 opacity-100'
              : 'translate-y-8 opacity-0'
          }`}
        >
          <h1 className='text-4xl md:text-5xl font-bold text-white mb-4'>
            Professional Experience
          </h1>
          <p className='text-xl text-gray-400 max-w-2xl mx-auto mb-8'>
            A journey through impactful roles in software development and system
            architecture
          </p>

          {/* Total Experience Stats */}
          <div className='flex flex-col sm:flex-row items-center justify-center gap-6 mb-8'>
            <div className='bg-gradient-to-r from-blue-500/20 to-teal-500/20 backdrop-blur-sm border border-blue-500/30 rounded-xl px-6 py-4 flex items-center gap-3 hover:from-blue-500/30 hover:to-teal-500/30 transition-all duration-300 group'>
              <div className='p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors duration-300'>
                <Briefcase className='w-5 h-5 text-blue-400' />
              </div>
              <div className='text-left'>
                <p className='text-gray-400 text-sm font-medium'>
                  Total Experience
                </p>
                <p className='text-white text-lg font-bold'>
                  {calculateTotalExperience()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className='relative'>
          {/* Timeline Line */}
          <div className='absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-teal-500 to-purple-500 opacity-30'></div>

          {/* Experience Cards */}
          <div className='space-y-8'>
            {experiences.map((experience, index) => {
              return (
                <div
                  key={experience.id}
                  ref={setItemRef(experience.id)}
                  data-id={experience.id}
                  className={`relative pl-16 md:pl-20 transition-opacity duration-500 ${
                    visibleItems.has(experience.id)
                      ? 'opacity-100'
                      : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {/* Timeline Dot */}
                  <div
                    className={`absolute left-3 md:left-5 w-6 h-6 rounded-full border-4 border-gray-900 transition-all duration-500 ${
                      visibleItems.has(experience.id)
                        ? 'bg-blue-500 shadow-lg shadow-blue-500/50'
                        : 'bg-gray-600'
                    }`}
                  ></div>

                  <ExperienceCard
                    experience={experience}
                    isVisible={visibleItems.has(experience.id)}
                    index={index}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceTimeline;
